/**
 * ProShip Direct v2 — Courier Priority.
 *
 * The setting for a merchant who does not want to write a rule. Most never will: they want to say
 * "prefer the cheapest" once and be done, and a rules table asks them to learn conditions, ordering
 * and strategies to express that. This is one choice, applied to everything.
 *
 * THIS REPLACES app/components/CourierPriority/CourierPriority.js, which offered the same three
 * rule types against the same endpoint. Both routes now render this page, so an existing link or
 * bookmark still works.
 *
 * REAL READS AND WRITES, not a fixture:
 *   read   merchant/{id} → mappedOrderRule.type, where the panel already reads it
 *   write  ruleengine/setMerchantRule with {merchantOid, ruleType}
 *
 * THE THIRD CHOICE IS A LIST, NOT A RULE. PACE and Cheapest are one click and the engine takes it
 * from there. Personalised is the merchant naming their couriers in the order they want them
 * tried, which is what a merchant who says "I just want my own priority" is asking for — no
 * conditions, no rule builder. Choosing it opens the picker in place rather than sending them to
 * another screen.
 *
 * WHAT IS AND IS NOT REAL HERE. PACE and Cheapest write to the rule engine. The courier order does
 * not: the engine models it (a rule with result {type:'Priority', values:[...]}) but exposes only
 * create_rule, so re-saving would stack duplicate rules on the account. The order is kept locally
 * until there is an endpoint that can replace one — see COURIER_PRIORITY_OPTIONS in data/defaults.
 *
 * NOT ALL FOUR REACH THE ENGINE YET. PACE and Cheapest write to the rule engine. Fastest has no
 * rule type anywhere in the panel, and a courier order cannot be replaced (create_rule creates but
 * nothing updates), so those two are held locally. The screen says so on the card rather than
 * letting a merchant believe a setting is live when it is not — see data/defaults.js for the two
 * backend TODOs that would close this.
 *
 * WHERE IT SITS IN THE ORDER OF PRECEDENCE, which the page states rather than assumes: rules are
 * evaluated first and win where they match; this decides everything they do not cover. For a
 * merchant with no rules, that is every order.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Typography, Button, Alert, Chip, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import { useCourierPriority, useShippingRules, usePricingModel, useCourierPool } from '../data/hooks';
import CourierPriorityPicker from '../components/CourierPriorityPicker';
import { COURIER_PRIORITY_OPTIONS } from '../data/defaults';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { formatDate } from '../../../helpers/UtilityHelper';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WHITE, SUCCESS, WARNING, AMBER_TEXT, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { SETTINGS_PATHS } from '../flags';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.55 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

/**
 * An order reduced to one string, so two of them can be compared.
 *
 * Both position and membership matter and nothing else does, which is exactly what joining the
 * ids gives. It also survives the array being rebuilt on every render — comparing references
 * would report a change on every keystroke elsewhere on the page.
 */
const orderKey = (list) => (list || []).join('|');

const ICONS = {
  PACE: AutoAwesomeOutlinedIcon,
  FASTEST: BoltOutlinedIcon,
  CHEAPEST: CurrencyRupeeOutlinedIcon,
  PERSONALIZED: TuneOutlinedIcon,
};

function OptionCard({ option, selected, disabled, busy, onSelect }) {
  const Icon = ICONS[option.key];

  return (
    <Box
      role='button'
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(option.key)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && onSelect(option.key)}
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.25,
        py: 1.1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: RADIUS.md,
        border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
        bgcolor: selected ? `${PRIMARY_BLUE}0a` : WHITE,
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': disabled ? undefined : { borderColor: PRIMARY_BLUE },
      }}
    >
      <Icon sx={{ fontSize: 18, color: selected ? PRIMARY_BLUE : SUBTLE, mt: '1px', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ ...strongSx, color: selected ? PRIMARY_BLUE : NAVY }}>
            {option.label}
          </Typography>
          {option.recommended && (
            <Chip
              size='small'
              label='recommended'
              sx={{
                height: 17,
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                fontWeight: WEIGHT.semibold,
                bgcolor: `${SUCCESS}14`,
                color: SUCCESS,
              }}
            />
          )}
        </Box>
        <Typography sx={smallSx}>{option.tagline}</Typography>
      </Box>
      {busy ? (
        <CircularProgress size={15} sx={{ flexShrink: 0 }} />
      ) : (
        selected && <CheckCircleIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, flexShrink: 0 }} />
      )}
    </Box>
  );
}

function CourierPriority() {
  const { option, order, setOrder, savedAt, loading, saving, error, choose } = useCourierPriority();
  const { rules } = useShippingRules();
  const { isBlended } = usePricingModel();
  const { pool, withheld } = useCourierPool();

  const [pending, setPending] = useState(null);
  const [saved, setSaved] = useState(null);

  /*
    THE ORDER IS NOW A DRAFT UNTIL IT IS SAVED.

    It used to write straight through: every drag, every add, every remove persisted the moment
    it happened. Nothing on screen said so, which left a merchant reordering nine couriers with
    no idea whether the arrangement was live — and left them no way back from a mis-drag except
    to reconstruct the previous order from memory.

    Ranking couriers is not one setting, it is a sequence of small moves that only means
    something when it is finished. So the moves are held here and committed once.
  */
  const [draft, setDraft] = useState(order);
  const [orderSaved, setOrderSaved] = useState(false);

  /*
    Re-seed the draft when the STORED order changes underneath it — first load, and the switch
    to Personalised. Keyed on the joined ids rather than the array, which is rebuilt each render
    and would otherwise re-seed forever and discard what the merchant just dragged.
  */
  const storedKey = orderKey(order);

  /*
    WHICH ORDER WE OURSELVES WROTE.

    Saving changes the stored order, which fires the re-seed below — so without this the save
    would clear its own confirmation in the same commit and the merchant would see the message
    flash and vanish, which reads exactly like a failure. Recording the key we wrote lets the
    re-seed tell our own write apart from a change that came from anywhere else.
  */
  const savedKeyRef = useRef(null);

  useEffect(() => {
    setDraft(order);
    if (savedKeyRef.current !== storedKey) setOrderSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey]);

  const dirty = orderKey(draft) !== storedKey;

  const saveOrder = () => {
    savedKeyRef.current = orderKey(draft);
    setOrder(draft);
    setOrderSaved(true);
  };

  /* Back to what is actually stored — the only undo a merchant has once they have dragged. */
  const discardOrder = () => {
    setDraft(order);
    setOrderSaved(false);
  };

  /* A confirmation that outlives the save would go stale the moment the next drag happens. */
  useEffect(() => {
    if (dirty) setOrderSaved(false);
  }, [dirty]);

  const activeRules = rules.filter((r) => r.enabled).length;
  const readOnly = isBlended;

  /** A confirmation only appears once the change has actually been accepted. */
  const pick = async (key) => {
    if (readOnly) return;
    setPending(key);
    setSaved(null);
    const ok = await choose(key);
    setPending(null);
    if (ok) setSaved(key);
  };

  useEffect(() => {
    if (!saved) return undefined;
    const timer = setTimeout(() => setSaved(null), 4000);
    return () => clearTimeout(timer);
  }, [saved]);

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='One preference for every shipment — no rules to write'>
        Courier Priority
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        {readOnly && (
          <Alert severity='info' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            You are on blended pricing, so courier choice is ProShip&rsquo;s to make. This is the
            preference in force — move to courier-wise pricing to change it yourself.
          </Alert>
        )}

        {error && (
          <Alert severity='error' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            {error}
          </Alert>
        )}

        {/* Precedence, stated before the choice. A merchant with rules needs to know this is the
            floor, not the whole story. */}
        {activeRules > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: GAP.sm,
              p: 1.25,
              borderRadius: RADIUS.md,
              bgcolor: LIGHT_BG,
              border: `1px solid ${BORDER}`,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: SUBTLE, mt: '2px' }} />
            <Typography sx={smallSx}>
              You have {activeRules} active courier rule{activeRules === 1 ? '' : 's'}. Those are
              checked first and win where they match — this decides every order they do not cover.{' '}
              <Box component={Link} to={SETTINGS_PATHS.rules} sx={{ color: PRIMARY_BLUE }}>
                See your rules
              </Box>
            </Typography>
          </Box>
        )}

        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 0.25, flexWrap: 'wrap' }}>
            <Typography sx={{ ...strongSx, fontSize: SIZE.sm }}>
              How should couriers be chosen?
            </Typography>
            {loading && <CircularProgress size={13} />}
          </Box>
          <Typography sx={{ ...smallSx, mb: 1.75 }}>
            Applies to every shipment, on your own couriers and ProShip&rsquo;s alike.
          </Typography>

          <Grid container spacing={1.25}>
            {COURIER_PRIORITY_OPTIONS.map((o) => (
              <Grid item xs={12} sm={6} md={3} key={o.key}>
                <OptionCard
                  option={o}
                  selected={option?.key === o.key}
                  disabled={readOnly || saving || loading}
                  busy={pending === o.key}
                  onSelect={pick}
                />
              </Grid>
            ))}
          </Grid>

          {saved && (
            <Alert severity='success' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
              {option?.custom
                ? 'Saved. Your courier order now decides every order no rule covers.'
                : `Saved. ${option?.label} now decides every order no rule covers.`}
            </Alert>
          )}

          {!loading && !option && !error && (
            <Alert severity='info' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
              No preference is set yet, so allocation runs on ProShip&rsquo;s own default. Pick one
              above to take it over.
            </Alert>
          )}

          {/* What the chosen one actually does, in full. */}
          {option && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.5,
                borderRadius: RADIUS.md,
                bgcolor: `${WARNING}0f`,
                border: `1px solid ${WARNING}44`,
              }}
            >
              <Typography sx={{ ...smallSx, color: NAVY }}>
                <b>{option.label}:</b> {option.note}
              </Typography>
              {option.caveat && (
                <Typography sx={{ ...smallSx, mt: 0.5, fontStyle: 'italic' }}>{option.caveat}</Typography>
              )}
              {option.pace && (
                <Typography sx={{ ...smallSx, mt: 0.5 }}>
                  Set what it weighs in{' '}
                  <Box component={Link} to={SETTINGS_PATHS.pace} sx={{ color: PRIMARY_BLUE }}>
                    PACE Engine
                  </Box>
                  .
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/*
          The order itself, in place. It only appears once Personalised is the live choice — shown
          alongside the other two it would read as a fourth setting rather than the detail of one.
        */}
        {option?.custom && (
          <Box sx={sectionCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 0.25, flexWrap: 'wrap' }}>
              <LayersOutlinedIcon sx={{ fontSize: 17, color: PRIMARY_BLUE }} />
              <Typography sx={{ ...strongSx, fontSize: SIZE.sm }}>Your courier order</Typography>
              <Box sx={{ flex: 1 }} />
              {dirty && (
                <Chip
                  size='small'
                  label='Unsaved changes'
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE['4xs'],
                    height: 20,
                    color: AMBER_TEXT,
                    bgcolor: `${WARNING}22`,
                  }}
                />
              )}
              {draft.length > 0 && (
                <Typography sx={smallSx}>
                  {draft.length} courier{draft.length === 1 ? '' : 's'} ranked
                </Typography>
              )}
            </Box>
            <Typography sx={{ ...smallSx, mb: 1.5 }}>
              Allocation tries these top to bottom and uses the first one that can serve the lane.
              A courier you leave out is never used.
            </Typography>

            <CourierPriorityPicker
              pool={pool}
              withheld={withheld}
              order={draft}
              onChange={setDraft}
              readOnly={readOnly}
            />

            {draft.length > 0 && draft.length < 2 && (
              <Alert severity='warning' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                Only one courier is ranked. If it cannot serve a lane there is no second choice, and
                the order will fail rather than fall back.
              </Alert>
            )}

            {/*
              SAVE AND DISCARD, always on screen rather than only when dirty.

              A button that appears when you touch something is a button you have to discover
              mid-task. Present and disabled, it answers the question the merchant actually has
              before they start dragging — "will I get to confirm this?" — which is the question
              that made this control feel untrustworthy when it saved silently.
            */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: GAP.sm,
                flexWrap: 'wrap',
                mt: 1.75,
                pt: 1.5,
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Button
                size='small'
                sx={primaryBtnSx}
                disabled={!dirty || readOnly}
                onClick={saveOrder}
                startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
              >
                Save order
              </Button>
              <Button
                size='small'
                sx={secondaryBtnSx}
                disabled={!dirty || readOnly}
                onClick={discardOrder}
                startIcon={<UndoOutlinedIcon sx={{ fontSize: 15 }} />}
              >
                Discard changes
              </Button>

              <Box sx={{ flex: 1 }} />

              {/*
                One line, three states: just saved, unsaved, or when it was last written. The
                last of those is the one that answers "did that save?" on a page opened fresh,
                and it is why savedAt is surfaced rather than left in the store.
              */}
              {orderSaved ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 15, color: SUCCESS }} />
                  <Typography sx={{ ...smallSx, color: SUCCESS }}>
                    Order saved. Allocation uses it for every shipment no rule covers.
                  </Typography>
                </Box>
              ) : dirty ? (
                <Typography sx={{ ...smallSx, color: AMBER_TEXT }}>
                  Not saved yet — allocation is still using your last saved order.
                </Typography>
              ) : savedAt ? (
                <Typography sx={smallSx}>Last saved {formatDate(savedAt, 'dd MMM yyyy, HH:mm')}</Typography>
              ) : null}
            </Box>
          </Box>
        )}

        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
            <Button component={Link} to={SETTINGS_PATHS.rules} size='small' sx={primaryBtnSx}>
              Need per-order control? Write a rule
            </Button>
            <Typography sx={smallSx}>
              A rule can send COD, a weight band or a zone to a different courier, leaving everything
              else on the preference above.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default withLayout(CourierPriority);
